import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useMyTeamRole } from '../../../team/useMyTeamRole';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import { useCampaignActions } from '../hooks/useCampaignActions';
import { useMailboxes } from '../hooks/useMailboxes';
import { useOutreachMailboxLimit } from '../hooks/useOutreachMailboxLimit';
import { MailboxGuideModal } from '../mailboxGuide/MailboxGuideModal';
import type { CampaignDetail } from '../outreachTypes';
import { MailboxPick, canSendFrom } from './MailboxPick';
import { QuestionPanel } from './QuestionPanel';
import { SendFromWhy } from './SendFromWhy';
import { StepBar } from './StepBar';
import { NEXT_LABELS } from './builderSteps';
import { sendFromMessages as messages } from './sendFromMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StepSendFromProps {
  accountId: number;
  campaign: CampaignDetail;
  readOnly: boolean;
  onBack: () => void;
  onNext: () => void;
}

/**
 * Step 4 "Send from" (design outreach-v2 e1, e2): the mailboxes the song goes from. One button,
 * "Connect your mailbox", opens the connect guide (frame o). Any member may connect a mailbox;
 * only the owner changes or removes one, in Settings. No Gmail sign-in (owner rule).
 */
const StepSendFromComponent: React.FC<StepSendFromProps> = ({ accountId, campaign, readOnly, onBack, onNext }) => {
  const intl = useIntl();
  const isOwner = useMyTeamRole() === 'OWNER';
  const { mailboxes } = useMailboxes(accountId);
  // The limit counts the IMAP/SMTP mailboxes of the whole workspace, as the API does
  // (`assertMailboxRoom`): the picker shows only the ones of this artist (final review I8).
  const { mailboxes: workspaceMailboxes } = useMailboxes();
  const limit = useOutreachMailboxLimit();
  const openUpgradePrompt = useUpgradePrompt();
  const { updateCampaign } = useCampaignActions(accountId);
  const [guideOpen, setGuideOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const picked = useMemo(() => new Set(campaign.mailboxUuids), [campaign.mailboxUuids]);
  const usablePicked = useMemo(
    () => mailboxes.filter((mailbox) => picked.has(mailbox.mailboxUuid) && canSendFrom(mailbox.health)),
    [mailboxes, picked]
  );
  const atLimit = useMemo(
    () => workspaceMailboxes.filter((mailbox) => mailbox.provider === 'imapSmtp').length >= limit.own,
    [limit.own, workspaceMailboxes]
  );

  const save = useCallback(
    (mailboxUuids: string[]) => {
      setFailed(false);
      updateCampaign({ campaignUuid: campaign.campaignUuid, mailboxUuids }).catch(() => setFailed(true));
    },
    [campaign.campaignUuid, updateCampaign]
  );
  const toggle = useCallback(
    (mailboxUuid: string) =>
      save(
        picked.has(mailboxUuid)
          ? campaign.mailboxUuids.filter((uuid) => uuid !== mailboxUuid)
          : [...campaign.mailboxUuids, mailboxUuid]
      ),
    [campaign.mailboxUuids, picked, save]
  );
  const connect = useCallback(() => {
    if (atLimit) {
      if (isOwner) {
        openUpgradePrompt(SMLUpgradeFeature.outreachMailboxes);
      }
      return;
    }
    setGuideOpen(true);
  }, [atLimit, isOwner, openUpgradePrompt]);

  const connectButton = (
    <SMLButton
      size="sm"
      color={mailboxes.length === 0 ? 'primary' : undefined}
      outline={mailboxes.length > 0}
      omitCap
      leftIcon={faPlus}
      disabled={readOnly || (atLimit && !isOwner)}
      onClick={connect}
      testID="builder-connect-mailbox"
    >
      {intl.formatMessage(atLimit ? messages.limitReached : messages.connect)}
    </SMLButton>
  );

  return (
    <>
      {mailboxes.length === 0 ? (
        <QuestionPanel
          title={intl.formatMessage(messages.connectTitle)}
          hint={intl.formatMessage(messages.connectHint)}
          testId="builder-step-send-from"
        >
          <div>{connectButton}</div>
        </QuestionPanel>
      ) : (
        <QuestionPanel
          title={intl.formatMessage(messages.pickTitle)}
          hint={intl.formatMessage(messages.pickHint)}
          right={
            <span className={shared.small}>
              {intl.formatMessage(messages.pickMeta, { count: mailboxes.length, limit: limit.own })}
            </span>
          }
          testId="builder-step-send-from"
        >
          <MailboxPick
            mailboxes={mailboxes}
            picked={picked}
            song={campaign.songTitle}
            readOnly={readOnly}
            onToggle={toggle}
          />
          <div className={shared.row}>
            {connectButton}
            <span className={shared.small}>{intl.formatMessage(messages.ownerOnly)}</span>
          </div>
        </QuestionPanel>
      )}
      {failed && <p className="text-danger mb-0">{intl.formatMessage(messages.saveFailed)}</p>}
      <SendFromWhy />
      <StepBar
        onBack={onBack}
        summary={
          usablePicked.length > 0
            ? intl.formatMessage(messages.barFrom, {
                addresses: intl.formatList(usablePicked.map((mailbox) => mailbox.address)),
              })
            : intl.formatMessage(mailboxes.length === 0 ? messages.barNone : messages.barPickOne)
        }
        nextLabel={intl.formatMessage(NEXT_LABELS.start)}
        nextDisabled={usablePicked.length === 0}
        onNext={onNext}
      />
      {guideOpen && (
        <MailboxGuideModal
          accountId={accountId}
          campaignUuid={campaign.campaignUuid}
          onClose={() => setGuideOpen(false)}
          onConnected={(mailboxUuid) => save([...new Set([...campaign.mailboxUuids, mailboxUuid])])}
        />
      )}
    </>
  );
};

export const StepSendFrom = TypedMemo(StepSendFromComponent);
